import { useOperatorDistanceUnit, useUsersCurrentAccessesQuery } from '@/api/queries/users';
import {
  ModalContextType,
  WrappedOrderDetailsModal,
} from '@/components/modals/OrderModal/WrappedOrderDetailsModal';
import Search from '@mui/icons-material/Search';
import { Button, Grid, InputAdornment, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useOperatorsRidesFilterQuery } from 'api/queries/operators';
import { PaginationQueryParams } from 'api/types';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  colors,
  ControlledField,
  DataTable,
  formatDateTimeLongWithLocale,
  UserPermission,
} from 'remote/global';
import useDebounce from 'utils/hooks/useDebounce';

export function ShowOrderDetailsModalLink({
  bookingNumber,
  rideId,
  text,
}: {
  bookingNumber: string;
  rideId: number;
  text: string;
}) {
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);

  return (
    <>
      <Button
        variant='text'
        onClick={() => setIsOrderDetailsModalOpen(true)}
        size='small'
        sx={{ fontSize: '14px', p: 0, height: 'auto' }}
      >
        {text}
      </Button>
      {isOrderDetailsModalOpen && (
        <WrappedOrderDetailsModal
          open={isOrderDetailsModalOpen}
          setOpen={setIsOrderDetailsModalOpen}
          onClose={() => setIsOrderDetailsModalOpen(false)}
          bookingNumber={bookingNumber}
          rideId={rideId}
          contextType={ModalContextType.ORDER_VIEW}
        />
      )}
    </>
  );
}

function LogisticsAllBookingsView() {
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const { control, watch, setValue } = useForm<PaginationQueryParams>({
    defaultValues: {
      search: '',
      pageNo: '0',
    },
  });
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions } = userInfo || {};
  const distanceUnit = useOperatorDistanceUnit();
  const [search, pageNo] = watch(['search', 'pageNo']);
  const debouncedSearch = useDebounce(search, 300);
  const { data: allRidesPage, isLoading } = useOperatorsRidesFilterQuery({
    search: debouncedSearch,
    pageNo,
    pageSize: '100',
  });

  const allOrders = useMemo(() => {
    const ridesArray = allRidesPage?.elements ?? []; // Count rides per bookingNumber

    const rideCountMap = new Map<string, number>();
    ridesArray.forEach((ride) => {
      const count = rideCountMap.get(ride.bookingNumber) ?? 0;
      rideCountMap.set(ride.bookingNumber, count + 1);
    }); // Deduplicate rides per bookingNumber, and attach rideCount

    const uniqueOrdersMap = new Map<string, any>();
    ridesArray.forEach((ride) => {
      if (!uniqueOrdersMap.has(ride.bookingNumber)) {
        uniqueOrdersMap.set(ride.bookingNumber, {
          ...ride,
          rideCount: rideCountMap.get(ride.bookingNumber) ?? 1,
        });
      }
    }); // Convert the Map values to an array and then take the first 20 rows.

    const combinedOrders = Array.from(uniqueOrdersMap.values());
    const limitedOrders = combinedOrders.slice(0, 20);

    return {
      elements: limitedOrders,
      pageCount: allRidesPage?.pageCount ?? 0,
      pageNo: allRidesPage?.pageNo ?? 0,
      pageSize: allRidesPage?.pageSize ?? 20,
      totalElements: allRidesPage?.totalElements ?? 0,
    };
  }, [allRidesPage]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'bookingNumber',
        headerName: `${t('LogisticsView.rides.table.header.bookingNumber')}`,
        flex: 1,
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <div>
            {' '}
            {[UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
              permissions?.order as UserPermission
            ) ? (
              <Typography sx={{ color: colors.gray9, fontSize: '14px' }}>
                #{row.bookingNumber}{' '}
              </Typography>
            ) : (
              <ShowOrderDetailsModalLink
                bookingNumber={row.bookingNumber}
                rideId={row.rideId}
                text={`#${row.bookingNumber} (${row.rideCount})`}
              />
            )}{' '}
          </div>
        ),
      },
      {
        field: 'estimatedStartAtLocal',
        headerName: `${t('LogisticsView.rides.table.header.startedAtDate')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
      },
      {
        field: 'estimatedEndAtLocal',
        headerName: `${t('LogisticsView.rides.table.header.estimatedEndAt')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
        valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
      },
      {
        field: 'clientStartAddress',
        headerName: `${t('LogisticsView.rides.table.header.clientStartAddress')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'clientEndAddress',
        headerName: `${t('LogisticsView.rides.table.header.clientEndAddress')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      {
        field: 'passengerCount',
        headerName: `${t('LogisticsView.rides.table.header.passengerCount')}`,
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
    ];
  }, [t, permissions?.order, i18n.language, distanceUnit]);

  return (
    <Grid container spacing={1}>
      <Grid item>
        <ControlledField
          control={control}
          name='search'
          fieldProps={{
            InputProps: {
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />{' '}
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>
      <Grid item sx={{ width: '100%' }}>
        <DataTable
          rows={allOrders}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.bookingNumber}
          onPageChange={(page) => setValue('pageNo', String(page))}
          t={t}
        />
      </Grid>
    </Grid>
  );
}

export default LogisticsAllBookingsView;
